<template>
  <v-container
    v-if="handoffInProgress"
    fluid
    class="d-flex justify-center align-center fill-height"
  >
    <p>Starting impersonation session…</p>
  </v-container>
  <v-container v-else fluid class="d-flex justify-center py-0 px-0">
    <AuthLayout :variant="layoutVariant">
      <LoginForm
        :logintype="'account'"
        @login="login($event)"
        :alert="alert"
        :accountlist="accountlist"
        :has-more-accounts="accountsHasMore"
        :is-loading-more-accounts="isLoadingMoreAccounts"
        :is-loading.sync="isLoading"
        :displayLogin="displayLogin"
        :redesign="redesign"
        :session-expired="sessionExpired"
        :recent-accounts="recentAccounts"
        :accounts-total="accountsTotal"
        :accounts-result-count="accountsCount"
        @onSelectAccount="onSelectAccount($event)"
        @closeError="alert = {}"
        @logout="logout"
        @load-more-accounts="onLoadMoreAccounts"
        @search-accounts="onSearchAccounts"
        :isUserClosedOrSuspended="isUserExist"
        :message="showMessage"
        :alertType="status"
        @backToLogin="onBackToLogin"
      />
    </AuthLayout>
  </v-container>
</template>

<script>
import LoginForm from "@components/login/LoginForm.vue";
import AuthLayout from "../components/AuthLayout/AuthLayout.vue";
import {
  loginEmailPW,
  getListofAccount,
  getAppConfig,
  exchangeImpersonation,
  accountLogin,
} from "@/api";
import {
  applyImpersonationAccountContext,
  IMPERSONATION_DURATION_MS,
} from "@components/utils/impersonationSession";
import { LD_FLAG_IDEA_MDP_276_IMPERSONATION } from "@components/constants/launchDarklyFlagKeys";
import { createNamespacedHelpers } from "vuex";
import * as Sentry from "@sentry/vue";
import configService from "@/services/config";
import launchDarklyService from "@/services/launchdarkly";
import { resolveDefaultLoggedInRedirect } from "@/utils/defaultLoggedInRedirect";
import {
  clearAllWizardBypasses,
  readWelcomeWizardIds,
  resolveWelcomeWizardEntry,
} from "@/utils/welcomeWizard";
import {
  LD_FLAG_IDEA_MDP_621,
  LD_FLAG_IDEA_MDP_656,
} from "@components/constants/launchDarklyFlagKeys";
import { resetInMemoryFilterState } from "@/utils/resetInMemoryFilterState";
import { switchAccount } from "@/utils/switchAccount";
import { sanitizeNextUrl, getNextUrlQueryParam, stripNextUrlQueryParam } from "@/utils/safeNextUrl";
const { mapState } = createNamespacedHelpers("isPosition");

const WRONG_CREDENTIALS_PATTERN = /email or password is incorrect/i;
const WRONG_CREDENTIALS_MESSAGE = "The email or password is not correct.";
const RECENT_ACCOUNTS_LIMIT = 2;

export default {
  name: "LoginView",

  components: {
    LoginForm,
    AuthLayout,
  },

  data() {
    return {
      alert: {
        type: "",
        message: "",
      },
      accountlist: [],
      accountsUserId: null,
      accountsPage: 1,
      accountsPageSize: 20,
      accountsHasMore: false,
      accountsSearch: "",
      accountsCount: 0,
      accountsTotal: null,
      recentAccounts: [],
      redesign: false,
      sessionExpired: false,
      accountsRequestId: 0,
      isLoadingMoreAccounts: false,
      displayLogin: true,
      isLoading: false,
      isUserExist: false,
      showMessage: "",
      status: "",
      handoffInProgress: false,
    };
  },
  async mounted() {
    if (this.$route.meta?.impersonationHandoff && this.$route.query.code) {
      const ideaMDP276 = await this.$getFlag(LD_FLAG_IDEA_MDP_276_IMPERSONATION, false);
      if (!ideaMDP276) {
        this.alert = {
          type: "error",
          message: "Impersonation is not available.",
        };
        return;
      }
      await this.completeImpersonationHandoff(this.$route.query.code);
    }
    this.redesign = (await this.$getFlag(LD_FLAG_IDEA_MDP_656, false)) === true;
    this.sessionExpired = this.redesign && this.$route?.query?.reason === "session_expired";
  },
  computed: {
    ...mapState(["positions", "activePositionsCount"]),
    layoutVariant() {
      if (!this.redesign) return "legacy";
      return this.isUserExist ? "card" : "split";
    },
  },
  methods: {
    async completeImpersonationHandoff(code) {
      const ideaMDP276 = await this.$getFlag(LD_FLAG_IDEA_MDP_276_IMPERSONATION, false);
      if (!ideaMDP276) {
        this.alert = {
          type: "error",
          message: "Impersonation is not available.",
        };
        return;
      }
      this.handoffInProgress = true;
      try {
        const { data } = await exchangeImpersonation(code);
        applyImpersonationAccountContext({
          ...data,
          expires_at:
            data.expires_at || new Date(Date.now() + IMPERSONATION_DURATION_MS).toISOString(),
          returnUrl: this.$route.query.return || "",
        });
        this.$store.commit("resetState");
        const accessPermission = JSON.parse(localStorage.getItem("accessPermission") || "{}");
        const route = await this.getPostLoginRoute(accessPermission);
        await this.$router.replace(route);
      } catch (error) {
        console.error("Impersonation handoff failed:", error);
        this.handoffInProgress = false;
        this.alert = {
          type: "error",
          message:
            error.response?.data?.detail ||
            error.response?.data?.error ||
            "Failed to start impersonation.",
        };
      }
    },
    async login(event) {
      this.isLoading = true;
      const normalizedEmail = event.email;
      event.email = normalizedEmail;
      loginEmailPW(event)
        .then(async (result) => {
          const user = result.data.user;
          if (
            result.data.user.state === "closed" ||
            result.data.user.state === "blocked" ||
            result.data.user.state === "suspended"
          ) {
            this.isUserExist = true;
            this.showMessage = `The user with email ${user.email} is closed/blocked/suspended at the level of the system.`;
            this.status = "error";
            return;
          }
          localStorage.clear();
          // A new sign-in is a new wizard session: a page skipped before the
          // sign-out must be offered again (rule 6.2, QA 2026-10-26).
          clearAllWizardBypasses();
          localStorage.setItem("token", result.data.token);
          localStorage.setItem("user", JSON.stringify(user));

          // Identify user in Sentry for better error tracking
          Sentry.setUser({
            id: user.id,
            email: user.email,
            username: user.username || user.email,
          });
          // Load secure configuration from backend after authentication
          try {
            await configService.loadSecureConfig(getAppConfig);
            console.log("Secure configuration loaded successfully after login");
          } catch (configError) {
            console.warn("Failed to load secure configuration:", configError);
          }

          // Re-init LaunchDarkly so a previously missing/empty SDK key picked up
          // from secure config is used before getPostLoginRoute reads flags.
          await launchDarklyService.initialize(user, true);

          this.accountsUserId = result.data.user.id;
          this.fetchUserAccounts({ offset: 0, search: "" })
            .then(async (res) => {
              if (!res) return;
              if (this.accountlist.length === 0) {
                localStorage.clear();
                this.alert = {
                  type: "error",
                  message: "You have no accounts permissions",
                };
              } else if (this.accountlist.length === 1 && !this.accountsHasMore) {
                const account = this.accountlist[0];
                const statusError = this.getAccountStatusError(account?.status);
                if (statusError) {
                  this.isUserExist = true;
                  this.showMessage = statusError;
                  this.status = "error";
                  return;
                }
                await this.onSelectAccount(account);
              } else {
                // ER-12027: when nextUrl names an account this user can access,
                // auto-select it and skip the picker. Otherwise show the picker
                // (onSelectAccount will still honour nextUrl after selection).
                const nextUrlAccountId = this.getSafeNextUrlAccountId();
                const account =
                  nextUrlAccountId != null
                    ? this.accountlist.find((a) => String(a.id) === String(nextUrlAccountId))
                    : null;
                if (account) {
                  const statusError = this.getAccountStatusError(account?.status);
                  if (statusError) {
                    this.isUserExist = true;
                    this.showMessage = statusError;
                    this.status = "error";
                    return;
                  }
                  await this.onSelectAccount(account);
                } else {
                  if (this.redesign) await this.loadRecentAccounts();
                  this.displayLogin = false;
                }
              }
            })
            .catch((err) => {
              const apiErrorMessage =
                err.response?.data?.error ||
                err.response?.data?.detail ||
                err.response?.data?.message ||
                (Array.isArray(err.response?.data?.non_field_errors)
                  ? err.response.data.non_field_errors[0]
                  : "") ||
                "Please try again, something went wrong.";

              const statusError = this.getAccountStatusErrorFromMessage(apiErrorMessage);
              if (statusError) {
                this.isUserExist = true;
                this.showMessage = statusError;
                this.status = "error";
                return;
              }

              this.alert = {
                type: "error",
                message: apiErrorMessage,
              };
            });
        })
        .catch((error) => {
          const apiErrorMessage =
            error.response?.data?.error ||
            error.response?.data?.detail ||
            error.response?.data?.message ||
            (Array.isArray(error.response?.data?.non_field_errors)
              ? error.response.data.non_field_errors[0]
              : "") ||
            "Please try again, something went wrong.";

          this.alert = {
            type: "error",
            message:
              this.redesign && WRONG_CREDENTIALS_PATTERN.test(apiErrorMessage)
                ? WRONG_CREDENTIALS_MESSAGE
                : apiErrorMessage,
          };
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async fetchUserAccounts({ offset = 0, search = "", append = false } = {}) {
      if (!this.accountsUserId) return null;
      const requestId = append ? this.accountsRequestId : ++this.accountsRequestId;
      const res = await getListofAccount(this.accountsUserId, {
        offset,
        limit: this.accountsPageSize,
        search,
      });
      if (requestId !== this.accountsRequestId) return null;

      const results = res.data?.results || [];
      if (append) {
        const existingIds = new Set(this.accountlist.map((account) => account.id));
        this.accountlist = [
          ...this.accountlist,
          ...results.filter((account) => !existingIds.has(account.id)),
        ];
      } else {
        this.accountlist = results;
      }

      this.accountsPage = Math.floor(offset / this.accountsPageSize) + 1;
      this.accountsSearch = search;
      const count = res.data?.count;
      const hasSearchQuery = String(search).trim() !== "";
      this.accountsHasMore = hasSearchQuery
        ? Boolean(res.data?.next)
        : Boolean(res.data?.next) || (typeof count === "number" && this.accountlist.length < count);
      this.accountsCount =
        typeof count === "number"
          ? count
          : this.accountlist.length + (this.accountsHasMore ? 1 : 0);
      if (!hasSearchQuery) this.accountsTotal = this.accountsCount;
      return res;
    },
    async loadRecentAccounts() {
      try {
        const res = await getListofAccount(this.accountsUserId, {
          offset: 0,
          limit: RECENT_ACCOUNTS_LIMIT,
          recent: true,
        });
        this.recentAccounts = (res.data?.results || [])
          .filter((account) => account.last_accessed_at)
          .slice(0, RECENT_ACCOUNTS_LIMIT);
      } catch (err) {
        console.error("error loading recent accounts: ", err);
        this.recentAccounts = [];
      }
    },
    async onLoadMoreAccounts() {
      if (!this.accountsHasMore || this.isLoadingMoreAccounts) return;
      this.isLoadingMoreAccounts = true;
      try {
        await this.fetchUserAccounts({
          offset: this.accountlist.length,
          search: this.accountsSearch,
          append: true,
        });
      } catch (err) {
        console.error("error loading more accounts: ", err);
      } finally {
        this.isLoadingMoreAccounts = false;
      }
    },
    async onSearchAccounts(searchValue) {
      if (!this.accountsUserId) return;
      const search = searchValue == null ? "" : String(searchValue);
      this.isLoadingMoreAccounts = true;
      try {
        await this.fetchUserAccounts({
          offset: 0,
          search,
          append: false,
        });
      } catch (err) {
        console.error("error searching accounts: ", err);
      } finally {
        this.isLoadingMoreAccounts = false;
      }
    },
    async onSelectAccount(account) {
      try {
        const statusError = this.getAccountStatusError(account.status);
        if (statusError) {
          this.isUserExist = true;
          this.showMessage = statusError;
          this.status = "error";
          return;
        }
        if (!account?.id) throw new Error("no account");
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) throw new Error("no user");

        if (this.redesign) await this.recordAccountSelection(account.id);

        // Account switch is shared with the deep-link router guard (ER-12027).
        const { rawAccessPermission } = await switchAccount(account, user, {
          emit: (event, data) => this.$root.$emit(event, data),
        });

        // Use raw API access for the landing route so patch-user-access cannot send
        // the user to Your Stuff when the API reports has_employee: false.
        const route = await this.getResolvedLoginRoute(rawAccessPermission || {});
        await this.$router.push(route);

        // Refresh flags/permissions after navigation so sync cannot race the redirect.
        this.$root.$emit("userLoggedIn");
      } catch (e) {
        console.error("error on login: ", e);
        this.$router.go(0);
      }
    },

    /** Stamps the account as just used so the picker's Recent section can list it (ER-12921). */
    async recordAccountSelection(accountId) {
      try {
        await accountLogin(accountId);
      } catch (err) {
        console.error("error recording account selection: ", err);
      }
    },

    getAccountStatusError(status) {
      const messages = {
        blocked: "Account is blocked, please contact EXiQtive support.",
        closed: "Account is closed, please contact EXiQtive support.",
        suspended: "Account is suspended, please contact EXiQtive support.",
      };
      return messages[status] || null;
    },
    getAccountStatusErrorFromMessage(message = "") {
      const normalizedMessage = String(message).toLowerCase();
      if (
        normalizedMessage.includes("access to this account is denied") ||
        (normalizedMessage.includes("access") && normalizedMessage.includes("denied"))
      ) {
        return this.getAccountStatusError("closed");
      }
      if (normalizedMessage.includes("suspend")) {
        return this.getAccountStatusError("suspended");
      }
      if (normalizedMessage.includes("block")) {
        return this.getAccountStatusError("blocked");
      }
      if (normalizedMessage.includes("closed") || normalizedMessage.includes("close")) {
        return this.getAccountStatusError("closed");
      }
      return null;
    },

    async getPostLoginRoute(currentAccount) {
      const newYourStuffEnabled = await this.$getFlag("my-stuff.new-your-stuff.enabled", false);
      const showNewOrganizationTab = true;
      const [showNewReadinessTab, showPerformanceTab, showFairPayTab, ideaMDP432] =
        await Promise.all([
          this.$getFlag("new-readiness", false),
          this.$getFlag("performance.enabled", false),
          this.$getFlag("fair-pay.enabled", false),
          this.$getFlag("idea-MDP-432", false),
        ]);

      return resolveDefaultLoggedInRedirect(currentAccount, {
        newYourStuffEnabled,
        showNewOrganizationTab,
        showNewReadinessTab,
        showPerformanceTab,
        showFairPayTab,
        ideaMDP432,
      });
    },
    getSafeNextUrl() {
      return sanitizeNextUrl(this.$route?.query?.nextUrl);
    },
    getSafeNextUrlAccountId() {
      const nextUrl = this.getSafeNextUrl();
      if (!nextUrl) return null;
      return getNextUrlQueryParam(nextUrl, "accountId");
    },
    async getResolvedLoginRoute(rawAccessPermission) {
      const nextUrl = this.getSafeNextUrl();
      if (nextUrl) {
        // accountId is resolved at login time (single/auto-select or picker), so it
        // must not leak into the URL and re-trigger the deep-link account guard.
        return stripNextUrlQueryParam(nextUrl, "accountId");
      }

      // ER-12245: the welcome wizard replaces the landing page until finished
      // (rule 3.6). A deep link above is not a landing page, so it still wins.
      const wizardPath = await this.resolveWelcomeWizardPath();
      if (wizardPath) {
        return wizardPath;
      }

      return this.getPostLoginRoute(rawAccessPermission || {});
    },
    /** Flag-gated, fail-open wizard entry (ER-12245 / MDP-621). */
    async resolveWelcomeWizardPath() {
      const enabled = await this.$getFlag(LD_FLAG_IDEA_MDP_621, false);
      if (!enabled) return null;
      const { accountId, accessId } = readWelcomeWizardIds();
      return resolveWelcomeWizardEntry({ accountId, accessId, enabled });
    },
    logout() {
      // ER-10191: drop in-memory filter/view singletons (SPA logout does not reload)
      resetInMemoryFilterState();
      // Clear Sentry user context
      Sentry.setUser(null);
      // Clear secure configuration
      configService.clearSecureConfig();
      localStorage.clear();
      // ER-12245: the wizard bypass is per browser session; signing out ends it.
      clearAllWizardBypasses();
      this.accountlist = [];
      this.accountsUserId = null;
      this.accountsPage = 1;
      this.accountsHasMore = false;
      this.accountsSearch = "";
      this.accountsCount = 0;
      this.accountsTotal = null;
      this.recentAccounts = [];
      this.isLoadingMoreAccounts = false;
      this.displayLogin = true;
    },
    onBackToLogin() {
      this.isUserExist = false;
    },
  },
};
</script>

<style scoped>
.user-login-form .login-bottom .ex-checkbox .icon {
  margin-top: 10px !important;
}
.user-login-form .login-bottom .ex-checkbox .label {
  margin-left: 21px !important;
}
.welcome-title {
  font-style: normal;
  font-size: 30px;
  line-height: 38px;
  font-weight: 700;
  text-align: center;
  color: #475467;
}
.welcome-subtitle {
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  color: #475467;
  font-weight: 700;
  text-align: center;
}
.login-custom-list {
  padding: 8px;
}
.login-picker .v-input__control .v-input__slot .v-text-field__slot .theme--light.v-label {
  padding-left: 0px !important;
}
</style>
